import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  Box,
  Typography,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TablePagination
} from '@mui/material';
import {
  Visibility as PreviewIcon,
  Download as DownloadIcon,
  MoreVert as MoreIcon,
  OpenInNew as OpenIcon,
  SelectAll as SelectAllIcon,
  GetApp as BulkDownloadIcon,
  Delete as DeleteIcon,
  DeleteSweep as BulkDeleteIcon
} from '@mui/icons-material';
import FileTypeIcon, { getFileCategory } from './FileTypeIcon';
import FilePreviewModal from './FilePreviewModal';
import DeleteDocumentDialog from './DeleteDocumentDialog';

const BucketDocumentsTable = ({
  documents = [],
  loading = false,
  sortBy,
  sortOrder,
  onSort,
  searchTerm,
  categoryFilter,
  onDeleteDocument,
  onDeleteDocuments
}) => {
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [previewDoc, setPreviewDoc] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextDoc, setContextDoc] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentsToDelete, setDocumentsToDelete] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Format file size utility
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  // Format date utility
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  // Clean file name utility
  const cleanFileName = (key) => {
    return key ? key.replace(/^tenants\/[^/]+\/uploads\//, '') : '';
  };

  // Filter and sort documents
  const processedDocuments = useMemo(() => {
    let filtered = documents;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(doc => {
        const fileName = cleanFileName(doc.key).toLowerCase();
        return fileName.includes(searchTerm.toLowerCase());
      });
    }

    // Apply category filter
    if (categoryFilter && categoryFilter !== 'all') {
      filtered = filtered.filter(doc => {
        const fileName = cleanFileName(doc.key);
        const category = getFileCategory(fileName);
        return category === categoryFilter;
      });
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'name':
          aValue = cleanFileName(a.key).toLowerCase();
          bValue = cleanFileName(b.key).toLowerCase();
          break;
        case 'size':
          aValue = a.size || 0;
          bValue = b.size || 0;
          break;
        case 'lastModified':
          aValue = new Date(a.lastModified || 0);
          bValue = new Date(b.lastModified || 0);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [documents, searchTerm, categoryFilter, sortBy, sortOrder]);

  // Paginated documents
  const paginatedDocuments = useMemo(() => {
    const start = page * rowsPerPage;
    return processedDocuments.slice(start, start + rowsPerPage);
  }, [processedDocuments, page, rowsPerPage]);

  // Handle selection
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedDocs(new Set(paginatedDocuments.map(doc => doc.key)));
    } else {
      setSelectedDocs(new Set());
    }
  };

  const handleSelectDoc = (docKey) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docKey)) {
      newSelected.delete(docKey);
    } else {
      newSelected.add(docKey);
    }
    setSelectedDocs(newSelected);
  };

  // Handle download
  const handleDownload = (doc) => {
    if (doc.url) {
      const link = document.createElement('a');
      link.href = doc.url;
      link.download = cleanFileName(doc.key);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle bulk download
  const handleBulkDownload = () => {
    const selectedDocuments = documents.filter(doc => selectedDocs.has(doc.key));
    selectedDocuments.forEach(doc => handleDownload(doc));
    setSelectedDocs(new Set());
  };

  // Handle context menu
  const handleContextMenu = (event, doc) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
    setContextDoc(doc);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setContextDoc(null);
  };

  // Handle sort
  const handleSort = (property) => {
    onSort(property);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle delete operations
  const handleDeleteSingle = (doc) => {
    setDocumentsToDelete([doc]);
    setDeleteDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleBulkDelete = () => {
    const selectedDocuments = documents.filter(doc => selectedDocs.has(doc.key));
    setDocumentsToDelete(selectedDocuments);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!documentsToDelete.length) return;

    setDeleteLoading(true);
    try {
      if (documentsToDelete.length === 1) {
        // Single delete
        await onDeleteDocument(documentsToDelete[0]);
      } else {
        // Bulk delete
        await onDeleteDocuments(documentsToDelete);
      }
      
      // Clear selections and close dialog
      setSelectedDocs(new Set());
      setDeleteDialogOpen(false);
      setDocumentsToDelete([]);
    } catch (error) {
      console.error('Delete failed:', error);
      // Error handling is done by parent component
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDocumentsToDelete([]);
  };

  const isAllSelected = paginatedDocuments.length > 0 && selectedDocs.size === paginatedDocuments.length;
  const isIndeterminate = selectedDocs.size > 0 && selectedDocs.size < paginatedDocuments.length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Typography>Loading documents...</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Bulk Actions */}
      {selectedDocs.size > 0 && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'primary.dark' }}>
              {selectedDocs.size} document{selectedDocs.size !== 1 ? 's' : ''} selected
            </Typography>
            <Button
              startIcon={<BulkDownloadIcon />}
              onClick={handleBulkDownload}
              size="small"
              variant="contained"
            >
              Download Selected
            </Button>
            <Button
              startIcon={<BulkDeleteIcon />}
              onClick={handleBulkDelete}
              size="small"
              variant="outlined"
              color="error"
              disabled={!onDeleteDocuments}
            >
              Delete Selected
            </Button>
            <Button
              onClick={() => setSelectedDocs(new Set())}
              size="small"
              color="inherit"
            >
              Clear Selection
            </Button>
          </Box>
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isIndeterminate}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  disabled={paginatedDocuments.length === 0}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  File Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'lastModified'}
                  direction={sortBy === 'lastModified' ? sortOrder : 'asc'}
                  onClick={() => handleSort('lastModified')}
                >
                  Last Modified
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'size'}
                  direction={sortBy === 'size' ? sortOrder : 'asc'}
                  onClick={() => handleSort('size')}
                >
                  Size
                </TableSortLabel>
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDocuments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchTerm || categoryFilter !== 'all' 
                      ? 'No documents match your filters' 
                      : 'No documents found'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedDocuments.map((doc) => {
                const fileName = cleanFileName(doc.key);
                const category = getFileCategory(fileName);
                const isSelected = selectedDocs.has(doc.key);

                return (
                  <TableRow
                    key={doc.key}
                    hover
                    selected={isSelected}
                    onContextMenu={(e) => handleContextMenu(e, doc)}
                    sx={{ cursor: 'context-menu' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectDoc(doc.key)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileTypeIcon fileName={fileName} size="medium" />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={fileName}
                        >
                          {fileName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(doc.lastModified)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatFileSize(doc.size)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={category.charAt(0).toUpperCase() + category.slice(1)}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => setPreviewDoc(doc)}
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(doc)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {onDeleteDocument && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSingle(doc)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="More options">
                          <IconButton
                            size="small"
                            onClick={(e) => handleContextMenu(e, doc)}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={processedDocuments.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100]}
        showFirstButton
        showLastButton
      />

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => {
          setPreviewDoc(contextDoc);
          handleCloseContextMenu();
        }}>
          <ListItemIcon>
            <PreviewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (contextDoc) handleDownload(contextDoc);
          handleCloseContextMenu();
        }}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          if (contextDoc?.url) {
            window.open(contextDoc.url, '_blank', 'noopener,noreferrer');
          }
          handleCloseContextMenu();
        }}>
          <ListItemIcon>
            <OpenIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in New Tab</ListItemText>
        </MenuItem>
        {onDeleteDocument && (
          <MenuItem 
            onClick={() => handleDeleteSingle(contextDoc)}
            sx={{ 
              color: 'error.main',
              '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Preview Modal */}
      <FilePreviewModal
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDocumentDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        documents={documentsToDelete}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />
    </>
  );
};

export default BucketDocumentsTable;